<template>
  <b-card no-body>
    <b-tabs
      pills
      card
      nav-class="px-2 pt-1 pb-0 flex-nowrap overflow-auto"
      nav-wrapper-class="border-bottom"
      content-class="p-0"
    >

      <!-- ══ Tab 1: By Recruiter (hidden in self scope) ═════════════ -->
      <b-tab v-if="scope !== 'self'" title="By Recruiter" :active="scope !== 'self'">
        <b-card-header class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between pt-75 pb-75 px-2" style="gap:8px">
          <span>
            <b-badge variant="primary" pill class="mr-25">{{ recruiterDisplayRows.length }}</b-badge>
            <span class="small text-muted">rows</span>
            <span class="small text-muted ml-50">· {{ verticalSummary.recruiterCount }} recruiters</span>
          </span>
          <div class="d-flex align-items-center flex-wrap" style="gap:6px">
            <span class="small text-muted">Per page:</span>
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              size="sm"
              style="width:70px"
              @change="currentPageRecruiter = 1"
            />
            <b-button size="sm" variant="outline-success" @click="exportCSV('recruiter')">
              <feather-icon icon="DownloadIcon" size="13" class="mr-25" />
              <span class="d-none d-sm-inline">Export CSV</span>
              <span class="d-sm-none">CSV</span>
            </b-button>
          </div>
        </b-card-header>

        <!-- Infra vs Service workload strip -->
        <div v-if="filteredRows.length" class="px-2 pt-1 pb-75">
          <div class="d-flex flex-wrap align-items-center justify-content-between" style="gap:8px">
            <span class="small font-weight-bold text-muted">Workload (PS share)</span>
            <div class="d-flex flex-wrap align-items-center" style="gap:6px">
              <span class="small font-weight-bold text-muted">Group:</span>
              <b-button
                v-for="v in recruiterVerticalOptions"
                :key="v.value"
                size="sm"
                :variant="recruiterVertical === v.value ? 'primary' : 'outline-secondary'"
                @click="recruiterVertical = v.value; currentPageRecruiter = 1"
              >
                {{ v.label }}
              </b-button>
            </div>
          </div>
          <div class="perf-mix-bar perf-mix-bar--lg mt-50" :title="verticalMixTitle">
            <span class="perf-mix-seg perf-mix-seg--infra" :style="{ width: verticalSummary.infraPct + '%' }" />
            <span class="perf-mix-seg perf-mix-seg--service" :style="{ width: verticalSummary.servicePct + '%' }" />
          </div>
          <div class="d-flex flex-wrap align-items-center mt-50" style="gap:10px">
            <span class="small">
              <b-badge variant="light-warning" class="mr-25">Infra</b-badge>
              <strong>{{ verticalSummary.infraPct }}%</strong>
              <span class="text-muted"> · {{ verticalSummary.infraPs }} PS · {{ verticalSummary.infraOnly + verticalSummary.both }} recruiters</span>
            </span>
            <span class="small">
              <b-badge variant="light-info" class="mr-25">Service</b-badge>
              <strong>{{ verticalSummary.servicePct }}%</strong>
              <span class="text-muted"> · {{ verticalSummary.servicePs }} PS · {{ verticalSummary.serviceOnly + verticalSummary.both }} recruiters · White only</span>
            </span>
            <span v-if="verticalSummary.both" class="small text-muted">{{ verticalSummary.both }} work both</span>
          </div>
          <div v-if="verticalSummary.hasCollar && verticalSummary.infraPs" class="d-flex flex-wrap align-items-center mt-50" style="gap:8px">
            <span class="small text-muted">Infra collar:</span>
            <b-badge variant="light-secondary">Blue {{ verticalSummary.infraBluePct }}%</b-badge>
            <b-badge variant="light-primary">White {{ verticalSummary.infraWhitePct }}%</b-badge>
          </div>
        </div>

        <b-table
          :items="recruiterDisplayRows"
          :fields="recruiterFields"
          :sort-by.sync="sortByRecruiter"
          :sort-desc.sync="sortDescRecruiter"
          :per-page="perPage"
          :current-page="currentPageRecruiter"
          small hover responsive class="mb-0"
        >
          <template #cell(total_revenue)="{ value }">
            <span class="font-weight-bold text-success">{{ formatRevenue(value) }}</span>
          </template>
          <template #cell(conversion)="{ item }">
            <span :class="conversionClass(item)">{{ conversionRate(item) }}%</span>
          </template>
          <template #cell(employee_status)="{ value }">
            <b-badge :variant="value === 'ACTIVE' ? 'light-success' : 'light-secondary'" pill>
              {{ value }}
            </b-badge>
          </template>
          <template #cell(job_vertical)="{ item }">
            <div class="d-flex flex-column align-items-start" style="gap:3px">
              <b-badge :variant="isInfraVertical(item.job_vertical) ? 'light-warning' : 'light-info'">
                {{ verticalShortLabel(item.job_vertical) }}
              </b-badge>
              <b-badge
                v-if="isServiceVertical(item.job_vertical)"
                variant="light-primary"
                pill
              >White</b-badge>
              <span v-else-if="hasCollarData && infraCollarChips(item).length" class="d-flex flex-wrap" style="gap:3px">
                <b-badge
                  v-for="chip in infraCollarChips(item)"
                  :key="chip.label"
                  :variant="chip.variant"
                  pill
                >{{ chip.label }}</b-badge>
              </span>
            </div>
          </template>
          <template #cell(mix_infra_pct)="{ item }">
            <div class="perf-mix-cell" style="min-width:132px">
              <div class="perf-mix-bar" :title="rowMixTitle(item)">
                <span class="perf-mix-seg perf-mix-seg--infra" :style="{ width: (item.mix_infra_pct || 0) + '%' }" />
                <span class="perf-mix-seg perf-mix-seg--service" :style="{ width: (item.mix_service_pct || 0) + '%' }" />
              </div>
              <div class="d-flex justify-content-between small mt-25">
                <span class="text-warning">{{ item.mix_infra_pct || 0 }}%</span>
                <span class="text-info">{{ item.mix_service_pct || 0 }}%</span>
              </div>
              <div v-if="hasCollarData && item.mix_infra_ps" class="small text-muted">
                Blue {{ item.mix_infra_blue_pct || 0 }}% · White {{ item.mix_infra_white_pct || 0 }}%
              </div>
            </div>
          </template>
        </b-table>

        <div class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between px-2 py-1 border-top" style="gap:6px">
          <span class="small text-muted">{{ pageInfo(recruiterDisplayRows.length, currentPageRecruiter) }}</span>
          <b-pagination
            v-model="currentPageRecruiter"
            :total-rows="recruiterDisplayRows.length"
            :per-page="perPage"
            size="sm" pills class="mb-0" first-number last-number
          />
        </div>
      </b-tab>

      <!-- ══ Tab 2: Month-wise (default when self scope) ════════════ -->
      <b-tab title="Month-wise" :active="scope === 'self'">
        <b-card-header class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between pt-75 pb-75 px-2" style="gap:8px">
          <span>
            <b-badge variant="info" pill class="mr-25">{{ filteredMonthlyRows.length }}</b-badge>
            <span class="small text-muted">rows</span>
          </span>
          <div class="d-flex align-items-center">
            <span class="small text-muted mr-50">Per page:</span>
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              size="sm"
              style="width:70px"
              class="mr-1"
              @change="currentPageMonthly = 1"
            />
            <b-button size="sm" variant="outline-success" @click="exportCSV('monthly')">
              <feather-icon icon="DownloadIcon" size="13" class="mr-25" />
              <span class="d-none d-sm-inline">Export CSV</span>
              <span class="d-sm-none">CSV</span>
            </b-button>
          </div>
        </b-card-header>

        <b-table
          :items="filteredMonthlyRows"
          :fields="monthlyFields"
          :sort-by.sync="sortByMonthly"
          :sort-desc.sync="sortDescMonthly"
          :per-page="perPage"
          :current-page="currentPageMonthly"
          small hover responsive class="mb-0"
        >
          <template #cell(month)="{ value }">
            <b-badge variant="light-secondary" class="text-nowrap">{{ formatMonth(value) }}</b-badge>
          </template>
          <template #cell(total_revenue)="{ value }">
            <span class="font-weight-bold text-success">{{ formatRevenue(value) }}</span>
          </template>
          <template #cell(conversion)="{ item }">
            <span :class="conversionClass(item)">{{ conversionRate(item) }}%</span>
          </template>
          <template #cell(employee_status)="{ value }">
            <b-badge :variant="value === 'ACTIVE' ? 'light-success' : 'light-secondary'" pill>
              {{ value }}
            </b-badge>
          </template>
          <template #cell(job_vertical)="{ value }">
            <b-badge :variant="value === 'Infra Industry' ? 'light-warning' : 'light-info'">
              {{ value === 'Infra Industry' ? 'Infra' : 'Service' }}
            </b-badge>
          </template>
        </b-table>

        <div class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between px-2 py-1 border-top" style="gap:6px">
          <span class="small text-muted">{{ pageInfo(filteredMonthlyRows.length, currentPageMonthly) }}</span>
          <b-pagination
            v-model="currentPageMonthly"
            :total-rows="filteredMonthlyRows.length"
            :per-page="perPage"
            size="sm" pills class="mb-0" first-number last-number
          />
        </div>
      </b-tab>

      <!-- ══ Tab 3: Client / Vertical Report ═════════════════════════ -->
      <b-tab title="Client-wise">
        <b-card-header class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between pt-75 pb-75 px-2" style="gap:8px">
          <div class="d-flex align-items-center">
            <div
              class="rounded p-50 mr-1 d-flex align-items-center justify-content-center bg-light-primary flex-shrink-0"
              style="width:32px;height:32px"
            >
              <feather-icon icon="BriefcaseIcon" size="14" class="text-primary" />
            </div>
            <div>
              <span class="font-weight-bold">Recruiter × Client Report</span>
              <small class="text-muted d-block">Totals by recruiter, client, and vertical across months in range</small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <span class="small text-muted mr-50">Per page:</span>
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              size="sm"
              style="width:70px"
              class="mr-1"
              @change="currentPageClient = 1"
            />
            <b-button size="sm" variant="outline-success" @click="exportCSV('client')">
              <feather-icon icon="DownloadIcon" size="13" class="mr-25" />
              <span class="d-none d-sm-inline">Export CSV</span>
              <span class="d-sm-none">CSV</span>
            </b-button>
          </div>
        </b-card-header>

        <!-- Info note about client data -->

        <!-- Vertical toggle filter -->
        <div class="px-2 pt-1 pb-0 d-flex flex-wrap align-items-center">
          <span class="small font-weight-bold text-muted mr-1">Filter vertical:</span>
          <b-button
            v-for="v in clientVerticalOptions"
            :key="v.value"
            size="sm"
            class="mr-25"
            :variant="clientVertical === v.value ? 'primary' : 'outline-secondary'"
            @click="clientVertical = v.value; currentPageClient = 1"
          >
            {{ v.label }}
          </b-button>
        </div>

        <b-table
          :items="clientReportRows"
          :fields="clientFields"
          :sort-by.sync="sortByClient"
          :sort-desc.sync="sortDescClient"
          :per-page="perPage"
          :current-page="currentPageClient"
          small hover responsive class="mb-0 mt-1"
        >
          <template #cell(job_vertical)="{ value }">
            <b-badge :variant="value === 'Infra Industry' ? 'light-warning' : 'light-info'">
              {{ value === 'Infra Industry' ? 'Infra' : 'Service' }}
            </b-badge>
          </template>
          <template #cell(total_revenue)="{ value }">
            <span class="font-weight-bold text-success">{{ formatRevenue(value) }}</span>
          </template>
          <template #cell(conversion)="{ item }">
            <span :class="conversionClass(item)">{{ conversionRate(item) }}%</span>
          </template>
          <template #cell(months_active)="{ value }">
            <b-badge variant="light-primary" pill>{{ value }}</b-badge>
          </template>
          <template #cell(employee_status)="{ value }">
            <b-badge :variant="value === 'ACTIVE' ? 'light-success' : 'light-secondary'" pill>
              {{ value }}
            </b-badge>
          </template>
        </b-table>

        <div class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between px-2 py-1 border-top" style="gap:6px">
          <span class="small text-muted">{{ pageInfo(clientReportRows.length, currentPageClient) }}</span>
          <b-pagination
            v-model="currentPageClient"
            :total-rows="clientReportRows.length"
            :per-page="perPage"
            size="sm" pills class="mb-0" first-number last-number
          />
        </div>
      </b-tab>

      <!-- ══ Tab: Event-level raw extract ════════════════════════════ -->
      <b-tab title="Raw Data">
        <b-card-header class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between pt-75 pb-75 px-2" style="gap:8px">
          <div class="d-flex align-items-center">
            <div
              class="rounded p-50 mr-1 d-flex align-items-center justify-content-center bg-light-secondary flex-shrink-0"
              style="width:32px;height:32px"
            >
              <feather-icon icon="ListIcon" size="14" class="text-secondary" />
            </div>
            <div>
              <span class="font-weight-bold">Event-level extract</span>
              <small class="text-muted d-block">
                One row per candidate × job × event — not rolled-up KPIs
              </small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <span class="small text-muted mr-50">Per page:</span>
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              size="sm"
              style="width:70px"
              class="mr-1"
              @change="currentPageEvent = 1"
            />
            <b-button
              size="sm"
              variant="outline-success"
              :disabled="!filteredEventRows.length"
              @click="exportCSV('event')"
            >
              <feather-icon icon="DownloadIcon" size="13" class="mr-25" />
              <span class="d-none d-sm-inline">Export CSV</span>
              <span class="d-sm-none">CSV</span>
            </b-button>
          </div>
        </b-card-header>

        <div v-if="eventLoading" class="text-center py-4">
          <b-spinner small variant="primary" />
          <p class="mt-1 mb-0 small text-muted">Loading raw event data…</p>
        </div>

        <b-alert v-else-if="eventError" show variant="warning" class="mx-2 mt-2 mb-2">
          {{ eventError }}
        </b-alert>

        <div v-else-if="!filteredEventRows.length" class="text-center py-4 px-2">
          <p class="mb-0 text-muted">No event rows for this date range / vertical.</p>
        </div>

        <template v-else>
          <div class="px-2 pt-1 pb-0">
            <b-badge variant="secondary" pill class="mr-25">{{ filteredEventRows.length }}</b-badge>
            <span class="small text-muted">event rows</span>
          </div>

          <b-table
            :items="filteredEventRows"
            :fields="eventFields"
            :sort-by.sync="sortByEvent"
            :sort-desc.sync="sortDescEvent"
            :per-page="perPage"
            :current-page="currentPageEvent"
            small hover responsive class="mb-0 mt-1"
          >
            <template #cell(event_month)="{ value }">
              <b-badge variant="light-secondary" class="text-nowrap">{{ formatMonth(value) }}</b-badge>
            </template>
            <template #cell(vertical)="{ value }">
              <b-badge :variant="value === 'Infra Industry' ? 'light-warning' : 'light-info'">
                {{ value === 'Infra Industry' ? 'Infra' : (value === 'Service Industry' ? 'Service' : value) }}
              </b-badge>
            </template>
            <template #cell(event_type)="{ value }">
              <b-badge :variant="eventTypeVariant(value)" pill>
                {{ eventTypeLabel(value) }}
              </b-badge>
            </template>
            <template #cell(revenue)="{ value }">
              <span v-if="+value" class="font-weight-bold text-success">{{ formatRevenue(value) }}</span>
              <span v-else class="text-muted">0</span>
            </template>
          </b-table>

          <div class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between px-2 py-1 border-top" style="gap:6px">
            <span class="small text-muted">{{ pageInfo(filteredEventRows.length, currentPageEvent) }}</span>
            <b-pagination
              v-model="currentPageEvent"
              :total-rows="filteredEventRows.length"
              :per-page="perPage"
              size="sm" pills class="mb-0" first-number last-number
            />
          </div>
        </template>
      </b-tab>

      <!-- ══ Tabs 4-8: Per-metric pivot reports ══════════════════════ -->
      <b-tab
        v-for="report in metricReports"
        :key="report.key"
        :title="report.tabLabel"
      >
        <b-card-header class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between pt-75 pb-75 px-2" style="gap:8px">
          <div class="d-flex align-items-center">
            <div
              class="rounded p-50 mr-1 d-flex align-items-center justify-content-center flex-shrink-0"
              :class="`bg-light-${report.color}`"
              style="width:32px;height:32px"
            >
              <feather-icon :icon="report.icon" size="14" :class="`text-${report.color}`" />
            </div>
            <div>
              <span class="font-weight-bold">{{ report.label }} Report</span>
              <small class="text-muted d-block">
                Grand Total:
                <strong :class="`text-${report.color}`">{{ report.grandTotalFormatted }}</strong>
              </small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <span class="small text-muted mr-50">Per page:</span>
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              size="sm"
              style="width:70px"
              class="mr-1"
              @change="pivotCurrentPages[report.key] = 1"
            />
            <b-button size="sm" variant="outline-success" @click="exportPivotCSV(report)">
              <feather-icon icon="DownloadIcon" size="13" class="mr-25" />
              <span class="d-none d-sm-inline">Export CSV</span>
              <span class="d-sm-none">CSV</span>
            </b-button>
          </div>
        </b-card-header>

        <b-table
          :items="report.rows"
          :fields="report.fields"
          :per-page="perPage"
          :current-page="pivotCurrentPages[report.key]"
          small hover responsive
          sort-by="total"
          :sort-desc="true"
          class="mb-0"
        >
          <template #cell(job_vertical)="{ value }">
            <b-badge :variant="value === 'Infra Industry' ? 'light-warning' : 'light-info'">
              {{ value === 'Infra Industry' ? 'Infra' : 'Service' }}
            </b-badge>
          </template>
          <template #cell(total)="{ value }">
            <strong :class="`text-${report.color}`">
              {{ report.isRevenue ? formatRevenue(value) : value }}
            </strong>
          </template>
          <template #cell()="{ field, value }">
            <span v-if="field.key.startsWith('m_')">
              <span v-if="value">{{ report.isRevenue ? formatRevenue(value) : value }}</span>
              <span v-else class="text-muted">—</span>
            </span>
            <span v-else>{{ value }}</span>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between px-2 py-1 border-top" style="gap:6px">
          <span class="small text-muted">
            {{ pageInfo(report.rows.length, pivotCurrentPages[report.key]) }}
          </span>
          <b-pagination
            v-model="pivotCurrentPages[report.key]"
            :total-rows="report.rows.length"
            :per-page="perPage"
            size="sm" pills class="mb-0" first-number last-number
          />
        </div>

        <!-- Column totals bar -->
        <div class="px-2 py-1 border-top bg-light d-flex flex-wrap">
          <span class="small font-weight-bold text-muted mr-1 mb-25">Column totals:</span>
          <span v-for="m in pivotMonths" :key="m" class="small mr-1 mb-25">
            <span class="text-muted">{{ formatMonth(m) }}:</span>
            <strong class="ml-25" :class="`text-${report.color}`">
              {{ report.isRevenue
                ? formatRevenue(report.monthTotals[m] || 0)
                : (report.monthTotals[m] || 0) }}
            </strong>
          </span>
        </div>
      </b-tab>

    </b-tabs>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader,
  BTabs, BTab,
  BTable, BPagination,
  BBadge, BButton,
  BFormSelect, BAlert, BSpinner,
} from 'bootstrap-vue'
import {
  formatRevenue,
  formatMonth,
  conversionRate,
  conversionClass,
  pageInfo,
  isInfraVertical,
  isServiceVertical,
  verticalShortLabel,
  hasPerformanceCollarData,
  buildRecruiterWorkload,
  buildVerticalWorkloadSummary,
} from '../perf-utils'

export default {
  name: 'PerfDataTabs',
  components: {
    BCard, BCardHeader,
    BTabs, BTab,
    BTable, BPagination,
    BBadge, BButton,
    BFormSelect, BAlert, BSpinner,
  },

  props: {
    filteredRows:       { type: Array, default: () => [] },
    filteredMonthlyRows:{ type: Array, default: () => [] },
    filteredRaw:        { type: Array, default: () => [] },
    filteredEventRows:  { type: Array, default: () => [] },
    eventLoading:       { type: Boolean, default: false },
    eventError:         { type: String, default: null },
    metricReports:      { type: Array, default: () => [] },
    pivotMonths:        { type: Array, default: () => [] },
    filterFrom:         { type: String, default: '' },
    filterTo:           { type: String, default: '' },
    scope:              { type: String, default: 'org' },
  },

  data() {
    return {
      perPage:     25,
      perPageOptions: [10, 25, 50, 100],

      // Per-tab current pages
      currentPageRecruiter: 1,
      currentPageMonthly:   1,
      currentPageClient:    1,
      currentPageEvent:     1,
      pivotCurrentPages: { revenue: 1, joiners: 1, offered: 1, shortlisted: 1, drops: 1 },

      // Per-tab sort state
      sortByRecruiter:   'total_revenue',
      sortDescRecruiter: true,
      sortByMonthly:     'month',
      sortDescMonthly:   true,
      sortByClient:      'joiners_count',
      sortDescClient:    true,
      sortByEvent:       'event_month',
      sortDescEvent:     true,

      // Client tab vertical filter
      clientVertical: 'All',
      clientVerticalOptions: [
        { value: 'All',             label: 'All' },
        { value: 'Infra Industry',  label: 'Infra' },
        { value: 'Service Industry',label: 'Service' },
      ],

      // By Recruiter group-by vertical
      recruiterVertical: 'All',
      recruiterVerticalOptions: [
        { value: 'All',              label: 'All' },
        { value: 'Infra Industry',   label: 'Infra' },
        { value: 'Service Industry', label: 'Service' },
      ],
    }
  },

  computed: {
    hasCollarData() {
      return hasPerformanceCollarData(this.filteredRaw)
    },

    recruiterMixByEmp() {
      return buildRecruiterWorkload(this.filteredRows, this.filteredRaw)
    },

    verticalSummary() {
      return buildVerticalWorkloadSummary(this.filteredRows, this.filteredRaw)
    },

    verticalMixTitle() {
      const s = this.verticalSummary
      return `Infra ${s.infraPct}% (${s.infraPs} PS) · Service ${s.servicePct}% (${s.servicePs} PS)`
    },

    recruiterDisplayRows() {
      const mix = this.recruiterMixByEmp
      return this.filteredRows
        .filter(r => this.recruiterVertical === 'All' || r.job_vertical === this.recruiterVertical)
        .map(r => {
          const m = mix[r.emp_id] || {}
          return {
            ...r,
            mix_infra_pct:        m.infraPct || 0,
            mix_service_pct:      m.servicePct || 0,
            mix_infra_ps:         m.infraPs || 0,
            mix_service_ps:       m.servicePs || 0,
            mix_infra_blue_pct:   m.infraBluePct || 0,
            mix_infra_white_pct:  m.infraWhitePct || 0,
            mix_label:            `Infra ${m.infraPct || 0}% / Service ${m.servicePct || 0}%`,
            mix_collar_label:     m.infraPs
              ? `Blue ${m.infraBluePct || 0}% / White ${m.infraWhitePct || 0}%`
              : '—',
          }
        })
    },

    // ── Recruiter table fields ────────────────────────────────────
    recruiterFields() {
      const fields = [
        { key: 'emp_id',               label: 'Emp ID',   sortable: true },
        { key: 'recruiter_name',       label: 'Recruiter', sortable: true },
        { key: 'team_lead',            label: 'TL',        sortable: true },
        { key: 'job_vertical',         label: 'Vertical',  sortable: true },
        { key: 'mix_infra_pct',        label: 'Mix',       sortable: true },
        { key: 'profile_shared_count', label: 'PS',        sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'shortlisted_count',    label: 'SL',        sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'offered_count',        label: 'Offered',   sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'joiners_count',        label: 'Joined',    sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'drops_count',          label: 'Drops',     sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'conversion',           label: 'Conv %',    sortable: false, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'total_revenue',        label: 'Revenue',   sortable: true },
        { key: 'employee_status',      label: 'Status',    sortable: true },
      ]
      return fields
    },

    // ── Monthly table fields ──────────────────────────────────────
    monthlyFields() {
      const base = [
        { key: 'month',                label: 'Month',     sortable: true },
        { key: 'profile_shared_count', label: 'PS',        sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'shortlisted_count',    label: 'SL',        sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'offered_count',        label: 'Offered',   sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'joiners_count',        label: 'Joined',    sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'drops_count',          label: 'Drops',     sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'conversion',           label: 'Conv %',    sortable: false, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'total_revenue',        label: 'Revenue',   sortable: true },
      ]
      if (this.scope === 'self') return base
      return [
        { key: 'emp_id',         label: 'Emp ID',    sortable: true },
        { key: 'recruiter_name', label: 'Recruiter', sortable: true },
        { key: 'team_lead',      label: 'TL',        sortable: true },
        { key: 'job_vertical',   label: 'Vertical',  sortable: true },
        ...base,
        { key: 'employee_status', label: 'Status',   sortable: true },
      ]
    },

    // ── Event-level raw extract fields (matches /raw SELECT *) ────
    eventFields() {
      return [
        { key: 'event_month',    label: 'Event Month', sortable: true },
        { key: 'emp_id',         label: 'Emp ID',      sortable: true },
        { key: 'recruiter_name', label: 'Recruiter',   sortable: true },
        { key: 'vertical',       label: 'Vertical',    sortable: true },
        { key: 'client_name',    label: 'Client',      sortable: true },
        { key: 'event_type',     label: 'Event Type',  sortable: true },
        { key: 'candidate_id',   label: 'Candidate',   sortable: true },
        { key: 'job_id',         label: 'Job',         sortable: true },
        { key: 'revenue',        label: 'Revenue',     sortable: true },
      ]
    },

    // ── Client-wise table fields ──────────────────────────────────
    clientFields() {
      return [
        { key: 'emp_id',               label: 'Emp ID',         sortable: true },
        { key: 'recruiter_name',       label: 'Recruiter',      sortable: true },
        { key: 'team_lead',            label: 'Team Lead',      sortable: true },
        { key: 'client_name',          label: 'Client',         sortable: true },
        { key: 'job_vertical',         label: 'Vertical',       sortable: true },
        { key: 'months_active',        label: 'Months Active',  sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'profile_shared_count', label: 'PS',             sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'shortlisted_count',    label: 'SL',             sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'offered_count',        label: 'Offered',        sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'joiners_count',        label: 'Joined',         sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'drops_count',          label: 'Drops',          sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'conversion',           label: 'Conv %',         sortable: false, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'total_revenue',        label: 'Revenue',        sortable: true },
        { key: 'employee_status',      label: 'Status',         sortable: true },
      ]
    },

    // ── Client report rows (recruiter × client × vertical, months active) ─
    clientReportRows() {
      const map = {}
      this.filteredRaw.forEach(r => {
        if (this.clientVertical !== 'All' && r.job_vertical !== this.clientVertical) return
        const clientLabel = String(r.client_name || '').trim() || '—'
        const key = `${r.emp_id}__${clientLabel}__${r.job_vertical}`
        if (!map[key]) {
          map[key] = {
            emp_id:               r.emp_id,
            recruiter_name:       r.recruiter_name,
            team_lead:            r['Team Lead'] || '—',
            client_name:          clientLabel,
            job_vertical:         r.job_vertical,
            employee_status:      r.employee_status,
            months_active:        new Set(),
            profile_shared_count: 0,
            shortlisted_count:    0,
            offered_count:        0,
            joiners_count:        0,
            drops_count:          0,
            total_revenue:        0,
          }
        }
        const rec = map[key]
        rec.months_active.add(r.month)
        rec.profile_shared_count += +r.profile_shared_count || 0
        rec.shortlisted_count    += +r.shortlisted_count    || 0
        rec.offered_count        += +r.offered_count        || 0
        rec.joiners_count        += +r.joiners_count        || 0
        rec.drops_count          += +r.drops_count          || 0
        rec.total_revenue        += +r.total_revenue        || 0
      })

      return Object.values(map).map(row => ({
        ...row,
        months_active: row.months_active.size,
      }))
    },
  },

  methods: {
    // Imported helpers exposed as methods so template can call them
    formatRevenue,
    formatMonth,
    conversionRate,
    conversionClass,
    isInfraVertical,
    isServiceVertical,
    verticalShortLabel,

    pageInfo(total, currentPage) {
      return pageInfo(total, currentPage, this.perPage)
    },

    rowMixTitle(item) {
      return `Infra ${item.mix_infra_pct || 0}% (${item.mix_infra_ps || 0} PS) · Service ${item.mix_service_pct || 0}% (${item.mix_service_ps || 0} PS)`
    },

    infraCollarChips(item) {
      const chips = []
      if (item.mix_infra_blue_pct) chips.push({ label: `Blue ${item.mix_infra_blue_pct}%`, variant: 'light-secondary' })
      if (item.mix_infra_white_pct) chips.push({ label: `White ${item.mix_infra_white_pct}%`, variant: 'light-primary' })
      return chips
    },

    eventTypeLabel(value) {
      const labels = {
        profile_shared: 'Profile Shared',
        shortlisted:    'Shortlisted',
        offered:        'Offered',
        joined:         'Joined',
        dropped:        'Dropped',
        revenue:        'Revenue',
      }
      return labels[value] || value || '—'
    },

    eventTypeVariant(value) {
      const variants = {
        profile_shared: 'light-primary',
        shortlisted:    'light-info',
        offered:        'light-warning',
        joined:         'light-success',
        dropped:        'light-danger',
        revenue:        'light-success',
      }
      return variants[value] || 'light-secondary'
    },

    exportCSV(mode) {
      let rows, fields
      if (mode === 'recruiter') {
        rows = this.recruiterDisplayRows
        fields = this.recruiterFields
          .filter(f => f.key !== 'conversion')
          .map(f => (f.key === 'mix_infra_pct' ? { key: 'mix_label', label: 'Mix' } : f))
        if (this.hasCollarData) {
          fields.splice(5, 0, { key: 'mix_collar_label', label: 'Infra collar' })
        }
      } else if (mode === 'monthly') {
        rows   = this.filteredMonthlyRows
        fields = this.monthlyFields.filter(f => f.key !== 'conversion')
      } else if (mode === 'event') {
        rows   = this.filteredEventRows
        fields = this.eventFields
      } else {
        rows   = this.clientReportRows
        fields = this.clientFields.filter(f => f.key !== 'conversion')
      }
      if (!rows.length) return
      this._downloadCSV(
        rows,
        fields.map(f => f.label),
        fields.map(f => f.key),
        `${mode}_${this.filterFrom}_to_${this.filterTo}.csv`,
      )
    },

    exportPivotCSV(report) {
      if (!report.rows.length) return
      const fields  = report.fields
      this._downloadCSV(
        report.rows,
        fields.map(f => f.label),
        fields.map(f => f.key),
        `${report.key}_report_${this.filterFrom}_to_${this.filterTo}.csv`,
        (k, v) => (k === 'total' || k.startsWith('m_')) && report.isRevenue
          ? this.formatRevenue(v) : (v ?? 0),
      )
    },

    _downloadCSV(rows, headers, keys, filename, valueFn) {
      const fmt = valueFn || ((k, v) => {
        if (k === 'total_revenue' || k === 'revenue') return +v || 0
        if (k === 'month' || k === 'event_month')     return this.formatMonth(v)
        return v ?? ''
      })
      const lines = [
        headers.join(','),
        ...rows.map(r =>
          keys.map(k => `"${String(fmt(k, r[k])).replace(/"/g, '""')}"`).join(',')
        ),
      ]
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },

    // Expose for parent to call if needed
    formatMonth(val) { return formatMonth(val) },
  },
}
</script>

<style scoped>
.perf-mix-bar {
  display: flex;
  overflow: hidden;
  height: 8px;
  border-radius: 999px;
  background: #ebe9f1;
}
.perf-mix-bar--lg {
  height: 10px;
}
.perf-mix-seg {
  display: block;
  min-width: 0;
  height: 100%;
}
.perf-mix-seg--infra {
  background: #ff9f43;
}
.perf-mix-seg--service {
  background: #00cfe8;
}
</style>
